/**
 * useModuleAccess(moduleName) → { read, create, update, del, loading, isAdmin }
 *
 * Single hook for page-level role gating (system-admin F27 / gmp-auditor F2). Reads the access
 * list the login flow stores in localStorage.user_access_data (from /bp/bp_get_employee_access_list),
 * applies the same parent-group inheritance the server uses (a role granted "Administration"
 * may use "User Management", …) and treats an admin (user_details.is_admin) as all-true.
 *
 * The server enforces the same flags (djangoapp/utilities/permissions.py) — this hook only
 * decides what to render; a 403 from the API is the real refusal.
 *
 *   const { create, update, del } = useModuleAccess('User Management');
 *   <Button disabled={!create}>Add</Button>
 */
import { useEffect, useMemo, useState } from 'react';
import { findParentGroupName } from '@/constants/accessModules';

const NONE = { read: false, create: false, update: false, del: false };
const ALL = { read: true, create: true, update: true, del: true };

const readStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw === 'undefined' || raw === 'null') return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const toFlags = (row) =>
  row
    ? {
        read: Boolean(row.read_access),
        create: Boolean(row.create_access),
        update: Boolean(row.update_access),
        del: Boolean(row.delete_access),
      }
    : null;

/** Non-hook variant for helpers / event handlers. */
export function getModuleAccess(moduleName) {
  const details = readStorage('user_details');
  if (details?.is_admin) return { ...ALL, isAdmin: true };
  const list = readStorage('user_access_data');
  if (!Array.isArray(list)) return { ...NONE, isAdmin: false };
  const own = toFlags(list.find((r) => r?.module_name === moduleName));
  const parentName = findParentGroupName(moduleName);
  const parent = parentName && parentName !== moduleName ? toFlags(list.find((r) => r?.module_name === parentName)) : null;
  if (!own && !parent) return { ...NONE, isAdmin: false };
  const merged = { ...NONE };
  ['read', 'create', 'update', 'del'].forEach((k) => {
    merged[k] = Boolean(own?.[k]) || Boolean(parent?.[k]);
  });
  return { ...merged, isAdmin: false };
}

export default function useModuleAccess(moduleName) {
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(() => readStorage('user_access_data') === null);

  useEffect(() => {
    // Re-evaluate when the login flow (or another tab) refreshes the access list.
    const onChange = (e) => {
      if (!e || !e.key || e.key === 'user_access_data' || e.key === 'user_details') {
        setLoading(readStorage('user_access_data') === null);
        setVersion((v) => v + 1);
      }
    };
    window.addEventListener('storage', onChange);
    window.addEventListener('user-access-updated', onChange);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('user-access-updated', onChange);
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const access = useMemo(() => getModuleAccess(moduleName), [moduleName, version]);
  return { ...access, loading };
}
